/*
 * /auth/practitioners (page)
 */
import {
	Badge,
	Button,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	InputFormField,
	PageHeader,
	PageWithEffect,
	SelectFormField,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	TextareaFormField,
	toast,
} from "@emr/react-ui"
import type { AuthUser } from "@emr/types"
import type { Address, Practitioner } from "@emr/db/server"
import { getPlatformMetadata } from "@emr/utils"
import { createFileRoute } from "@tanstack/react-router"
import {
	AlertCircle,
	Loader2,
	Pencil,
	Plus,
	Trash2,
	UserCheck,
} from "lucide-react"
import { useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import { adminPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/practitioners")({
	component: PractitionersPage,
	loader: adminPageLoader,
})

const STAFF_ROLES = ["practitioner", "nurse"] as const

const CANADIAN_PROVINCES = [
	{ value: "AB", label: "Alberta" },
	{ value: "BC", label: "British Columbia" },
	{ value: "MB", label: "Manitoba" },
	{ value: "NB", label: "New Brunswick" },
	{ value: "NL", label: "Newfoundland and Labrador" },
	{ value: "NS", label: "Nova Scotia" },
	{ value: "NT", label: "Northwest Territories" },
	{ value: "NU", label: "Nunavut" },
	{ value: "ON", label: "Ontario" },
	{ value: "PE", label: "Prince Edward Island" },
	{ value: "QC", label: "Quebec" },
	{ value: "SK", label: "Saskatchewan" },
	{ value: "YT", label: "Yukon" },
]

/* ─── Schemas ────────────────────────────────────────────────────────────── */

const APPT_LENGTH_OPTIONS = [
	{ value: "15", label: "15 min" },
	{ value: "20", label: "20 min" },
	{ value: "30", label: "30 min" },
	{ value: "45", label: "45 min" },
	{ value: "60", label: "60 min" },
	{ value: "90", label: "90 min" },
	{ value: "120", label: "120 min" },
]

const profileSchema = z.object({
	firstName: z.string().min(1, "Required"),
	middleName: z.string().optional(),
	lastName: z.string().min(1, "Required"),
	professionalDesignation: z.string().optional(),
	specialization: z.string().optional(),
	defaultAppointmentMinutes: z.string().optional(),
	email: z.string().optional(),
	phone: z.string().optional(),
	phone2: z.string().optional(),
	fax: z.string().optional(),
	website: z.string().optional(),
	biography: z.string().optional(),
})

const licenseSchema = z.object({
	province: z.string().min(1, "Required"),
	college: z.string().optional(),
	licenseNumber: z.string().min(1, "Required"),
	expiresAt: z.string().optional(),
})

const addressSchema = z.object({
	type: z.string().default("clinic"),
	line1: z.string().min(1, "Required"),
	line2: z.string().optional(),
	city: z.string().min(1, "Required"),
	state: z.string().optional(),
	postalCode: z.string().optional(),
	country: z.string().default("CA"),
})

type ProfileValues = z.infer<typeof profileSchema>
type LicenseValues = z.infer<typeof licenseSchema>
type AddressValues = z.infer<typeof addressSchema>

/* ─── Page ───────────────────────────────────────────────────────────────── */

function PractitionersPage() {
	const { data: allUsers = [], isLoading: usersLoading } =
		trpc.user.listUsers.useQuery()

	const {
		data: records = [],
		isLoading: recordsLoading,
		refetch,
	} = trpc.practitioner.getAllByFilter.useQuery({})

	const isLoading = usersLoading || recordsLoading

	const staffUsers = allUsers.filter((u) => {
		const role = getPlatformMetadata(u)?.role
		return STAFF_ROLES.includes(role as any)
	})

	const recordByUserId = new Map(
		(records ?? []).filter((r) => r.userId).map((r) => [r.userId!, r]),
	)

	const [editing, setEditing] = useState<{
		user: AuthUser
		record: Practitioner | null
	} | null>(null)

	return (
		<PageWithEffect effect={"aurora"} centerVertical={false}>
			<Container width={"xxl"} padding={"lg"} gap={"lg"}>
				<PageHeader title={"Practitioners"} />

				{isLoading && (
					<div className={"flex items-center justify-center py-16"}>
						<Loader2 className={"size-6 animate-spin text-muted-foreground"} />
					</div>
				)}

				{!isLoading && staffUsers.length === 0 && (
					<p className={"py-12 text-center text-sm text-muted-foreground"}>
						No practitioners found. Assign a user the Practitioner or Nurse role
						in User Management first.
					</p>
				)}

				{!isLoading && staffUsers.length > 0 && (
					<PractitionerTable
						users={staffUsers}
						recordByUserId={recordByUserId}
						onEdit={(user, record) => setEditing({ user, record })}
					/>
				)}
			</Container>

			{editing && (
				<PractitionerDialog
					user={editing.user}
					record={editing.record}
					open
					onOpenChange={(o) => !o && setEditing(null)}
					onProfileSaved={(updated) => {
						refetch()
						setEditing((prev) => prev && { ...prev, record: updated })
					}}
				/>
			)}
		</PageWithEffect>
	)
}

/* ─── Table ──────────────────────────────────────────────────────────────── */

function PractitionerTable({
	users,
	recordByUserId,
	onEdit,
}: {
	users: AuthUser[]
	recordByUserId: Map<string, Practitioner>
	onEdit: (user: AuthUser, record: Practitioner | null) => void
}) {
	return (
		<div className={"w-full overflow-x-auto rounded-lg border"}>
			<table className={"w-full text-sm"}>
				<thead>
					<tr className={"border-b bg-muted/40 text-left"}>
						<th className={"px-4 py-3 font-medium"}>Name / Email</th>
						<th className={"px-4 py-3 font-medium"}>Role</th>
						<th className={"px-4 py-3 font-medium"}>Record</th>
						<th className={"px-4 py-3 font-medium"}>Designation</th>
						<th className={"px-4 py-3 font-medium"}>Specialization</th>
						<th className={"px-4 py-3 font-medium"} />
					</tr>
				</thead>
				<tbody>
					{users.map((u) => {
						const record = recordByUserId.get(u.id) ?? null
						const role = getPlatformMetadata(u)?.role
						const displayName =
							u.user_metadata?.displayName ?? u.email?.split("@")[0]

						return (
							<tr
								key={u.id}
								className={"border-b last:border-0 hover:bg-muted/20"}
							>
								<td className={"px-4 py-3"}>
									<p className={"font-medium text-foreground"}>{displayName}</p>
									<p className={"text-xs text-muted-foreground"}>{u.email}</p>
								</td>
								<td className={"px-4 py-3"}>
									<Badge
										variant={"secondary"}
										className={"text-xs capitalize"}
									>
										{role}
									</Badge>
								</td>
								<td className={"px-4 py-3"}>
									{record ? (
										<Badge variant={"secondary"} className={"gap-1 text-xs"}>
											<UserCheck className={"size-3"} />
											Complete
										</Badge>
									) : (
										<Badge
											variant={"outline"}
											className={
												"gap-1 border-amber-400 text-xs text-amber-600"
											}
										>
											<AlertCircle className={"size-3"} />
											Missing
										</Badge>
									)}
								</td>
								<td className={"px-4 py-3 text-muted-foreground"}>
									{record?.professionalDesignation ?? (
										<span className={"opacity-30"}>—</span>
									)}
								</td>
								<td className={"px-4 py-3 text-muted-foreground"}>
									{record?.specialization ?? (
										<span className={"opacity-30"}>—</span>
									)}
								</td>
								<td className={"px-4 py-3 text-right"}>
									<Button
										variant={"ghost"}
										size={"sm"}
										onClick={() => onEdit(u, record)}
									>
										{record ? (
											<>
												<Pencil className={"mr-1.5 size-3"} />
												Edit
											</>
										) : (
											<>
												<Plus className={"mr-1.5 size-3"} />
												Create Record
											</>
										)}
									</Button>
								</td>
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

/* ─── Main Dialog ────────────────────────────────────────────────────────── */

function PractitionerDialog({
	user,
	record,
	open,
	onOpenChange,
	onProfileSaved,
}: {
	user: AuthUser
	record: Practitioner | null
	open: boolean
	onOpenChange: (o: boolean) => void
	onProfileSaved: (updated: Practitioner) => void
}) {
	const displayName = user.user_metadata?.displayName ?? user.email ?? ""

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className={"flex max-h-[90vh] flex-col sm:max-w-2xl"}>
				<DialogHeader className={"shrink-0"}>
					<DialogTitle>
						{record ? "Edit Practitioner" : "Create Practitioner Record"}
					</DialogTitle>
					<p className={"text-sm text-muted-foreground"}>{displayName}</p>
				</DialogHeader>

				<Tabs
					defaultValue={"profile"}
					className={"flex min-h-0 flex-1 flex-col"}
				>
					<TabsList className={"shrink-0 self-start"}>
						<TabsTrigger value={"profile"}>Profile</TabsTrigger>
						<TabsTrigger value={"licenses"} disabled={!record}>
							Licenses
						</TabsTrigger>
						<TabsTrigger value={"address"} disabled={!record}>
							Address
						</TabsTrigger>
					</TabsList>

					<div className={"min-h-0 flex-1 overflow-y-auto"}>
						<TabsContent value={"profile"} className={"mt-4"}>
							<ProfileTab
								user={user}
								record={record}
								onSaved={onProfileSaved}
							/>
						</TabsContent>

						{record && (
							<>
								<TabsContent value={"licenses"} className={"mt-4"}>
									<LicensesTab practitionerId={record.id} />
								</TabsContent>

								<TabsContent value={"address"} className={"mt-4"}>
									<AddressTab practitionerId={record.id} />
								</TabsContent>
							</>
						)}
					</div>
				</Tabs>
			</DialogContent>
		</Dialog>
	)
}

/* ─── Profile Tab ────────────────────────────────────────────────────────── */

function ProfileTab({
	user,
	record,
	onSaved,
}: {
	user: AuthUser
	record: Practitioner | null
	onSaved: (updated: Practitioner) => void
}) {
	const displayName =
		user.user_metadata?.displayName ?? user.email?.split("@")[0] ?? ""
	const spaceIdx = displayName.indexOf(" ")
	const defaultFirst =
		spaceIdx !== -1 ? displayName.substring(0, spaceIdx) : displayName
	const defaultLast =
		spaceIdx !== -1 ? displayName.substring(spaceIdx + 1) : ""

	const create = trpc.practitioner.create.useMutation({
		onError: (e) => toast.error(e.message),
	})
	const update = trpc.practitioner.update.useMutation({
		onError: (e) => toast.error(e.message),
	})
	const isPending = create.isPending || update.isPending

	async function handleSubmit(values: ProfileValues) {
		const clean = {
			...values,
			email: values.email || null,
			phone: values.phone || null,
			phone2: values.phone2 || null,
			fax: values.fax || null,
			website: values.website || null,
			middleName: values.middleName || null,
			professionalDesignation: values.professionalDesignation || null,
			specialization: values.specialization || null,
			biography: values.biography || null,
			defaultAppointmentMinutes: values.defaultAppointmentMinutes
				? parseInt(values.defaultAppointmentMinutes, 10)
				: null,
		}
		try {
			if (record) {
				const result = await update.mutateAsync({ id: record.id, ...clean })
				onSaved(result as Practitioner)
			} else {
				const result = await create.mutateAsync({
					userId: user.id,
					...clean,
					firstName: clean.firstName!,
					lastName: clean.lastName!,
				})
				onSaved(result as Practitioner)
				toast.success(
					"Record created. Use the Licenses and Address tabs to add more details.",
				)
			}
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	return (
		<Form<ProfileValues>
			schema={profileSchema}
			defaultValues={{
				firstName: record?.firstName ?? defaultFirst,
				middleName: record?.middleName ?? "",
				lastName: record?.lastName ?? defaultLast,
				professionalDesignation: record?.professionalDesignation ?? "",
				specialization: record?.specialization ?? "",
				defaultAppointmentMinutes: record?.defaultAppointmentMinutes
					? String(record.defaultAppointmentMinutes)
					: "",
				email: record?.email ?? user.email ?? "",
				phone: record?.phone ?? "",
				phone2: record?.phone2 ?? "",
				fax: record?.fax ?? "",
				website: record?.website ?? "",
				biography: record?.biography ?? "",
			}}
			onSubmit={handleSubmit}
			className={"flex flex-col gap-5 pb-4"}
		>
			<section>
				<p className={"mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground"}>
					Name
				</p>
				<div className={"grid grid-cols-3 gap-3"}>
					<InputFormField name={"firstName"} label={"First Name"} placeholder={"Jane"} />
					<InputFormField name={"middleName"} label={"Middle Name"} placeholder={"A."} />
					<InputFormField name={"lastName"} label={"Last Name"} placeholder={"Smith"} />
				</div>
			</section>

			<section>
				<p className={"mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground"}>
					Professional
				</p>
				<div className={"grid grid-cols-3 gap-3"}>
					<InputFormField
						name={"professionalDesignation"}
						label={"Designation"}
						placeholder={"MD, NP, RN…"}
					/>
					<InputFormField
						name={"specialization"}
						label={"Specialization"}
						placeholder={"General Practice"}
					/>
					<SelectFormField
						name={"defaultAppointmentMinutes"}
						label={"Default Appt Length"}
						options={APPT_LENGTH_OPTIONS}
						placeholder={"Select…"}
					/>
				</div>
			</section>

			<section>
				<p className={"mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground"}>
					Contact
				</p>
				<div className={"grid grid-cols-2 gap-3"}>
					<InputFormField name={"email"} label={"Email"} placeholder={"jane@clinic.com"} />
					<InputFormField name={"phone"} label={"Phone"} placeholder={"+1 (555) 000-0000"} />
					<InputFormField name={"phone2"} label={"Phone 2"} placeholder={"+1 (555) 000-0001"} />
					<InputFormField name={"fax"} label={"Fax"} placeholder={"+1 (555) 000-0002"} />
					<InputFormField
						name={"website"}
						label={"Website"}
						placeholder={"https://clinic.com"}
						className={"col-span-2"}
					/>
				</div>
			</section>

			<section>
				<p className={"mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground"}>
					Biography
				</p>
				<TextareaFormField
					name={"biography"}
					label={""}
					placeholder={"Short professional biography…"}
					rows={3}
				/>
			</section>

			<DialogFooter>
				<Button type={"submit"} disabled={isPending}>
					{isPending && <Loader2 className={"mr-2 size-4 animate-spin"} />}
					{record ? "Save Changes" : "Create Record"}
				</Button>
			</DialogFooter>
		</Form>
	)
}

/* ─── Licenses Tab ───────────────────────────────────────────────────────── */

function LicensesTab({ practitionerId }: { practitionerId: string }) {
	const { data: licenses = [], refetch } =
		trpc.practitionerLicense.getAllByFilter.useQuery({
			where: { practitionerId, active: true },
		})

	const create = trpc.practitionerLicense.create.useMutation({
		onSuccess: () => { toast.success("License added"); refetch() },
		onError: (e) => toast.error(e.message),
	})
	const remove = trpc.practitionerLicense.delete.useMutation({
		onSuccess: () => { toast.success("License removed"); refetch() },
		onError: (e) => toast.error(e.message),
	})

	async function handleAdd(values: LicenseValues) {
		try {
			await create.mutateAsync({
				practitionerId,
				province: values.province,
				college: values.college || null,
				licenseNumber: values.licenseNumber,
				expiresAt: values.expiresAt ? new Date(values.expiresAt) : null,
			})
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	return (
		<div className={"flex flex-col gap-6 pb-4"}>
			{/* Existing licenses */}
			{(licenses ?? []).length > 0 && (
				<div className={"rounded-lg border"}>
					<table className={"w-full text-sm"}>
						<thead>
							<tr className={"border-b bg-muted/40 text-left"}>
								<th className={"px-3 py-2 font-medium"}>Province</th>
								<th className={"px-3 py-2 font-medium"}>College</th>
								<th className={"px-3 py-2 font-medium"}>License #</th>
								<th className={"px-3 py-2 font-medium"}>Expires</th>
								<th className={"px-3 py-2"} />
							</tr>
						</thead>
						<tbody>
							{(licenses ?? []).map((lic) => (
								<tr key={lic.id} className={"border-b last:border-0"}>
									<td className={"px-3 py-2 font-medium"}>{lic.province}</td>
									<td className={"px-3 py-2 text-muted-foreground"}>
										{lic.college ?? <span className={"opacity-30"}>—</span>}
									</td>
									<td className={"px-3 py-2"}>{lic.licenseNumber}</td>
									<td className={"px-3 py-2 text-muted-foreground"}>
										{lic.expiresAt
											? new Date(lic.expiresAt).toLocaleDateString("en-CA")
											: <span className={"opacity-30"}>—</span>}
									</td>
									<td className={"px-3 py-2 text-right"}>
										<Button
											variant={"ghost"}
											size={"icon"}
											className={"size-7 text-destructive hover:text-destructive"}
											disabled={remove.isPending}
											onClick={() => remove.mutate({ id: lic.id })}
										>
											<Trash2 className={"size-3.5"} />
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{(licenses ?? []).length === 0 && (
				<p className={"text-center text-sm text-muted-foreground"}>
					No licenses on file.
				</p>
			)}

			{/* Add license form */}
			<div>
				<p className={"mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground"}>
					Add License
				</p>
				<Form<LicenseValues>
					schema={licenseSchema}
					defaultValues={{ province: "", college: "", licenseNumber: "", expiresAt: "" }}
					onSubmit={handleAdd}
					resetOnSuccess
					className={"flex flex-col gap-3"}
				>
					<div className={"grid grid-cols-2 gap-3"}>
						<SelectFormField
							name={"province"}
							label={"Province"}
							options={CANADIAN_PROVINCES}
							placeholder={"Select province"}
						/>
						<InputFormField
							name={"college"}
							label={"Regulatory College"}
							placeholder={"e.g. CPSO, CNO"}
						/>
					</div>
					<div className={"grid grid-cols-2 gap-3"}>
						<InputFormField
							name={"licenseNumber"}
							label={"License Number"}
							placeholder={"e.g. 123456"}
						/>
						<InputFormField
							name={"expiresAt"}
							label={"Expiry Date"}
							type={"date"}
						/>
					</div>
					<div>
						<Button type={"submit"} size={"sm"} disabled={create.isPending}>
							{create.isPending && (
								<Loader2 className={"mr-2 size-4 animate-spin"} />
							)}
							Add License
						</Button>
					</div>
				</Form>
			</div>
		</div>
	)
}

/* ─── Address Tab ────────────────────────────────────────────────────────── */

function AddressTab({ practitionerId }: { practitionerId: string }) {
	const { data: addresses = [], refetch } =
		trpc.address.getAllByFilter.useQuery({
			where: { practitionerId, active: true },
		})

	const existing = (addresses ?? [])[0] as Address | undefined

	const create = trpc.address.create.useMutation({
		onSuccess: () => { toast.success("Address saved"); refetch() },
		onError: (e) => toast.error(e.message),
	})
	const update = trpc.address.update.useMutation({
		onSuccess: () => { toast.success("Address saved"); refetch() },
		onError: (e) => toast.error(e.message),
	})
	const isPending = create.isPending || update.isPending

	async function handleSubmit(values: AddressValues) {
		try {
			if (existing) {
				await update.mutateAsync({
					id: existing.id,
					...values,
					line2: values.line2 || null,
					state: values.state || null,
					postalCode: values.postalCode || null,
				})
			} else {
				await create.mutateAsync({
					practitionerId,
					...values,
					line2: values.line2 || null,
					state: values.state || null,
					postalCode: values.postalCode || null,
				})
			}
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	return (
		<Form<AddressValues>
			schema={addressSchema}
			defaultValues={{
				type: existing?.type ?? "clinic",
				line1: existing?.line1 ?? "",
				line2: existing?.line2 ?? "",
				city: existing?.city ?? "",
				state: existing?.state ?? "",
				postalCode: existing?.postalCode ?? "",
				country: existing?.country ?? "CA",
			}}
			onSubmit={handleSubmit}
			className={"flex flex-col gap-3 pb-4"}
		>
			<InputFormField
				name={"line1"}
				label={"Street Address"}
				placeholder={"123 Main St"}
			/>
			<InputFormField
				name={"line2"}
				label={"Suite / Unit"}
				placeholder={"Suite 200"}
			/>
			<div className={"grid grid-cols-2 gap-3"}>
				<InputFormField name={"city"} label={"City"} placeholder={"Toronto"} />
				<SelectFormField
					name={"state"}
					label={"Province"}
					options={CANADIAN_PROVINCES}
					placeholder={"Select province"}
				/>
			</div>
			<div className={"grid grid-cols-2 gap-3"}>
				<InputFormField
					name={"postalCode"}
					label={"Postal Code"}
					placeholder={"M5V 2T6"}
				/>
				<InputFormField
					name={"country"}
					label={"Country"}
					placeholder={"CA"}
				/>
			</div>

			<DialogFooter>
				<Button type={"submit"} disabled={isPending}>
					{isPending && <Loader2 className={"mr-2 size-4 animate-spin"} />}
					{existing ? "Save Address" : "Add Address"}
				</Button>
			</DialogFooter>
		</Form>
	)
}
